import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { FiGlobe, FiRefreshCw, FiCheck, FiCopy, FiAlertCircle, FiClock, FiShield } from 'react-icons/fi';
import { toast } from '../../Toast';
import { SENDING_HEALTH } from './announcementData';
import { SectionProps } from './AnnouncementSettings';

interface Props extends SectionProps {
    checking: boolean;
    setChecking: (checking: boolean) => void;
}

type RecordStatus = 'found' | 'recommended' | 'checking';

// preview of a DNS check: records tick over one by one, then the last check time updates
const CHECK_STEP_MS = 1200;

const Domain: React.FC<Props> = ({ form, setForm, checking, setChecking }) => {
    const [checked, setChecked] = useState(0);
    const [lastChecked, setLastChecked] = useState('today at 9:12 am');
    const set = (field: keyof typeof form.domain, value: string) => setForm(f => ({ ...f, domain: { ...f.domain, [field]: value } }));

    const records: { type: string; host: string; value: string; done: RecordStatus }[] = [
        { type: 'TXT', host: '@', value: 'v=spf1 include:spf.maileroo.com ~all', done: 'found' },
        { type: 'CNAME', host: 'mlr._domainkey', value: 'dkim.maileroo.com', done: 'found' },
        { type: 'CNAME', host: 'mlr2._domainkey', value: 'dkim2.maileroo.com', done: 'found' },
        { type: 'CNAME', host: 'bounce', value: 'mail.maileroo.com', done: 'found' },
        { type: 'TXT', host: '_dmarc', value: `v=DMARC1; p=none; rua=mailto:${form.domain.replyTo || form.domain.from}`, done: 'recommended' },
    ];
    const status = (i: number): RecordStatus => checking && i >= checked ? 'checking' : records[i].done;
    const found = records.filter(r => r.done === 'found').length;

    useEffect(() => {
        if (!checking) return;
        if (checked >= records.length) {
            setChecking(false);
            setLastChecked(`today at ${moment().format('h:mm a')}`);
            toast.success(`DNS checked · ${found} of ${records.length} records found (preview)`);
            return;
        }
        const t = setTimeout(() => setChecked(c => c + 1), CHECK_STEP_MS);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checking, checked]);

    // leaving the Domain section stops the preview check (the nav status would otherwise stay on "Checking…")
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => () => setChecking(false), []);

    const check = () => {
        setChecked(0);
        setChecking(true);
    };

    const copy = (r: { host: string; value: string }) => {
        navigator.clipboard?.writeText(r.value).then(
            () => toast.success(`Copied the ${r.host} record`),
            () => toast.error('Could not copy — select the value and copy it by hand'));
    };

    const checkButton = (cls: string) => <span className={cls}>
        <button type="button" className="btn-outline with-icon" onClick={check} disabled={checking}>
            <FiRefreshCw className={checking ? 'spin' : ''} /> {checking ? 'Checking…' : 'Check DNS Again'}
        </button>
    </span>;

    return <>
        <section className="card">
            <div className="section-title">
                <span className="icon-tile"><FiGlobe /></span>
                <div className="text">
                    <h2>Send from Your Own Domain</h2>
                    <div className="hint">Investi sends through Maileroo. These records prove the domain is yours.</div>
                </div>
                {checkButton('in-title')}
            </div>

            {checking
                ? <div className="domain-status checking">
                    <span className="icon-tile"><FiRefreshCw className="spin" /></span>
                    <div className="text">
                        <strong>Checking your DNS records…</strong>
                        <span>Asking the internet's name servers · {Math.min(checked, records.length)} of {records.length} checked · usually takes 10–20 seconds</span>
                    </div>
                </div>
                : <div className="domain-status ok">
                    <span className="icon-tile"><FiCheck /></span>
                    <div className="text">
                        <strong>Domain verified — sending from {form.domain.from}</strong>
                        <span>{found} of {records.length} records found · last checked {lastChecked} · checked again automatically every day</span>
                    </div>
                </div>}

            <div className="fields three">
                <label className="field">
                    <span className="label">Sending domain</span>
                    <input value={form.domain.domain} onChange={e => set('domain', e.target.value)} />
                </label>
                <label className="field">
                    <span className="label">Emails come from</span>
                    <input type="email" value={form.domain.from} onChange={e => set('from', e.target.value)} />
                </label>
                <label className="field">
                    <span className="label">Replies go to</span>
                    <input type="email" value={form.domain.replyTo} onChange={e => set('replyTo', e.target.value)} />
                </label>
            </div>

            <strong className="records-title">DNS records to add at your domain provider</strong>
            <div className="dns">
                <div className="tr th">
                    <span className="type">Type</span>
                    <span className="host">Name / host</span>
                    <span className="value">Value</span>
                    <span className="status">Status</span>
                    <span className="copy" />
                </div>
                {records.map((r, i) => {
                    const s = status(i);
                    return <div key={r.host} className="tr">
                        <span className="type"><span className="badge plain">{r.type}</span></span>
                        <span className="host mono">{r.host}</span>
                        <span className="value mono" title={r.value}>{r.value}</span>
                        <span className={`status ${s}`}>
                            {s === 'found' && <><FiCheck /> Found</>}
                            {s === 'recommended' && <><FiAlertCircle /> Recommended</>}
                            {s === 'checking' && <><FiClock /> Checking…</>}
                        </span>
                        <span className="copy"><button type="button" className="icon-btn" title="Copy value" aria-label={`Copy the ${r.host} record`} onClick={() => copy(r)}><FiCopy /></button></span>
                    </div>;
                })}
            </div>
            {checkButton('at-bottom')}
        </section>

        <section className="card">
            <div className="section-title">
                <span className="icon-tile"><FiShield /></span>
                <div className="text"><h2>Sending Health</h2><div className="hint">Last 30 days</div></div>
            </div>
            <div className="health">
                {SENDING_HEALTH.map(h => <div key={h.label}><span>{h.label}</span><FiCheck /><strong>{h.value}</strong></div>)}
                <div><span>Provider</span><strong>Maileroo</strong></div>
            </div>
        </section>
    </>;
};

export default Domain;
